/**
 * /recruitment/candidates/:id — candidate profile (ADR-0012). Details + consent,
 * CV upload/download (D-8 — the allow-list is surfaced and a 415 is handled
 * gracefully), the candidate's applications, and per-application offers (create
 * offer with the currency 422 surfaced; the extend-offer gate; convert trigger).
 *
 * HR-gated; mounted under the admin layout.
 */
import { createRoute, Link } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import {
  createOffer,
  CV_ACCEPT,
  CV_ALLOWED_LABEL,
  downloadCv,
  getCandidate,
  listApplications,
  listCvs,
  listOffers,
  offerAction,
  requestExtendOffer,
  uploadCv,
  type Application,
  type Offer,
} from '../api/recruitment.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { formatBytes, formatDate, formatMoney } from '../lib/format.js';
import type { CurrencyCode } from '@kora/types';

export const recruitmentCandidateDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/recruitment/candidates/$candidateId',
  component: RecruitmentCandidateDetailPage,
});

const CURRENCIES = ['NGN', 'GHS', 'CDF', 'USD'] as const;
const PAY_FREQUENCIES = ['monthly', 'weekly', 'daily', 'hourly'] as const;

function RecruitmentCandidateDetailPage(): JSX.Element {
  const { candidateId } = recruitmentCandidateDetailRoute.useParams();
  const candidate = useQuery({ queryKey: ['recruitment', 'candidate', candidateId], queryFn: () => getCandidate(candidateId) });

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <Link to="/recruitment/candidates" className="text-sm text-gray-400 underline">← Candidates</Link>
      {candidate.isLoading ? (
        <Placeholder>Loading…</Placeholder>
      ) : candidate.isError ? (
        <Placeholder error>Could not load this candidate.</Placeholder>
      ) : (
        <>
          <header className="space-y-1">
            <h1 className="font-serif text-3xl">{candidate.data!.firstName} {candidate.data!.lastName}</h1>
            <p className="text-sm text-gray-400">
              {candidate.data!.email ?? 'no email'} · {candidate.data!.countryCode}
              {candidate.data!.source ? ` · ${candidate.data!.source}` : ''}
            </p>
            <p className="text-xs">
              {candidate.data!.anonymizedAt ? (
                <span className="text-gray-500">Anonymised {formatDate(candidate.data!.anonymizedAt)}</span>
              ) : candidate.data!.consentGivenAt ? (
                <span className="text-green-400">Consent on file</span>
              ) : (
                <span className="text-yellow-400">No consent recorded</span>
              )}
            </p>
          </header>

          {candidate.data!.anonymizedAt ? (
            <Placeholder>This candidate has been anonymised; CV upload and offers are disabled.</Placeholder>
          ) : (
            <>
              <CvSection candidateId={candidateId} />
              <ApplicationsSection candidateId={candidateId} />
            </>
          )}
        </>
      )}
    </section>
  );
}

function CvSection({ candidateId }: { candidateId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const cvs = useQuery({ queryKey: ['recruitment', 'cv', candidateId], queryFn: () => listCvs(candidateId) });

  const upload = useMutation({
    mutationFn: (file: File) => uploadCv(candidateId, file),
    onSuccess: async () => {
      setError(null);
      if (fileRef.current) fileRef.current.value = '';
      await queryClient.invalidateQueries({ queryKey: ['recruitment', 'cv', candidateId] });
    },
    onError: (e) => {
      // A 415 means the file type isn't allowed — surface it as guidance, not a crash.
      if (e instanceof ApiError && e.status === 415) setError(`That file type isn’t accepted. Allowed: ${CV_ALLOWED_LABEL}.`);
      else setError(e instanceof ApiError ? e.message : 'Upload failed.');
    },
  });

  return (
    <div className="rounded border border-gray-900 p-5 space-y-3">
      <h2 className="text-sm uppercase tracking-wide text-gray-500">CV</h2>
      <input
        ref={fileRef}
        type="file"
        accept={CV_ACCEPT}
        aria-label="Upload CV"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); }}
        disabled={upload.isPending}
        className="block text-sm text-gray-300"
      />
      <p className="text-xs text-gray-500">Accepted: {CV_ALLOWED_LABEL}. Max 10 MB.</p>
      {error ? <Alert variant="error">{error}</Alert> : null}

      {cvs.isLoading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : (cvs.data?.artifacts ?? []).length === 0 ? (
        <p className="text-xs text-gray-500">No CV uploaded yet.</p>
      ) : (
        <ul className="space-y-1">
          {cvs.data!.artifacts.map((a) => (
            <li key={a.id} className="flex items-center justify-between text-sm">
              <span className="text-gray-200">{a.filename} <span className="text-gray-500 text-xs">({formatBytes(a.sizeBytes)})</span></span>
              <button type="button" onClick={() => void downloadCv(candidateId, a.id, a.filename)} className="text-xs text-gray-300 underline">Download</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ApplicationsSection({ candidateId }: { candidateId: string }): JSX.Element {
  const apps = useQuery({ queryKey: ['recruitment', 'applications', 'candidate', candidateId], queryFn: () => listApplications({ candidateId }) });
  return (
    <div className="space-y-3">
      <h2 className="text-sm uppercase tracking-wide text-gray-500">Applications</h2>
      {apps.isLoading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : (apps.data?.applications ?? []).length === 0 ? (
        <p className="text-xs text-gray-500">No applications. Add this candidate to a requisition from the pipeline.</p>
      ) : (
        apps.data!.applications.map((app) => <ApplicationCard key={app.id} app={app} />)
      )}
    </div>
  );
}

function ApplicationCard({ app }: { app: Application }): JSX.Element {
  const offers = useQuery({ queryKey: ['recruitment', 'offers', app.id], queryFn: () => listOffers(app.id) });
  return (
    <div className="rounded border border-gray-900 p-4 space-y-3">
      <p className="text-sm text-white">Application <span className="text-gray-500 text-xs ml-1">{app.status}</span></p>
      <CreateOfferForm applicationId={app.id} />
      {(offers.data?.offers ?? []).map((o) => <OfferRow key={o.id} offer={o} applicationId={app.id} />)}
    </div>
  );
}

function CreateOfferForm({ applicationId }: { applicationId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [grossSalary, setGrossSalary] = useState('');
  const [currency, setCurrency] = useState<(typeof CURRENCIES)[number]>('NGN');
  const [payFrequency, setPayFrequency] = useState<(typeof PAY_FREQUENCIES)[number]>('monthly');

  const mut = useMutation({
    mutationFn: () => createOffer(applicationId, { grossSalary: grossSalary.trim(), currency, payFrequency }),
    onSuccess: async () => { setGrossSalary(''); await queryClient.invalidateQueries({ queryKey: ['recruitment', 'offers', applicationId] }); },
  });

  return (
    <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (grossSalary.trim()) mut.mutate(); }}>
      <label className="text-xs text-gray-400 space-y-1"><span>Gross salary</span>
        <input value={grossSalary} onChange={(e) => setGrossSalary(e.target.value)} aria-label="Gross salary" placeholder="500000" className="w-28 rounded border border-gray-700 bg-gray-900 px-2 py-1 text-white text-sm" />
      </label>
      <label className="text-xs text-gray-400 space-y-1"><span>Currency</span>
        <select value={currency} onChange={(e) => setCurrency(e.target.value as (typeof CURRENCIES)[number])} aria-label="Currency" className="rounded border border-gray-700 bg-gray-900 px-2 py-1 text-white text-sm">
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>
      <label className="text-xs text-gray-400 space-y-1"><span>Frequency</span>
        <select value={payFrequency} onChange={(e) => setPayFrequency(e.target.value as (typeof PAY_FREQUENCIES)[number])} aria-label="Pay frequency" className="rounded border border-gray-700 bg-gray-900 px-2 py-1 text-white text-sm">
          {PAY_FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      </label>
      <button type="submit" disabled={!grossSalary.trim() || mut.isPending} className="rounded bg-white text-black px-3 py-1 text-sm font-medium hover:bg-gray-200 disabled:opacity-50">
        {mut.isPending ? 'Creating…' : 'Create offer'}
      </button>
      <ApiErrorNotice error={mut.error} fallback="Could not create the offer." className="w-full" />
    </form>
  );
}

function OfferRow({ offer, applicationId }: { offer: Offer; applicationId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['recruitment', 'offers', applicationId] });

  const approve = useMutation({ mutationFn: () => offerAction(offer.id, 'approve'), onSuccess: () => void refresh() });
  const requestExtend = useMutation({ mutationFn: () => requestExtendOffer(offer.id), onSuccess: () => void refresh() });
  const accept = useMutation({ mutationFn: () => offerAction(offer.id, 'accept'), onSuccess: () => void refresh() });
  const decline = useMutation({ mutationFn: () => offerAction(offer.id, 'decline'), onSuccess: () => void refresh() });

  return (
    <div className="rounded border border-gray-800 bg-gray-950 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-gray-200">{formatMoney(offer.grossSalary, offer.currency as CurrencyCode)} / {offer.payFrequency}</span>
        <span className="rounded px-2 py-0.5 text-xs border border-gray-700 text-gray-300">{offer.status}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {offer.status === 'draft' ? (
          <button type="button" onClick={() => approve.mutate()} disabled={approve.isPending} className="rounded border border-gray-700 text-xs px-3 py-1 hover:bg-gray-900 disabled:opacity-50">Approve</button>
        ) : null}
        {offer.status === 'approved' ? (
          <button type="button" onClick={() => requestExtend.mutate()} disabled={requestExtend.isPending} className="rounded bg-blue-700 text-white text-xs px-3 py-1 disabled:opacity-50">Request extend</button>
        ) : null}
        {offer.status === 'pending_extend_approval' ? (
          <span className="text-xs text-yellow-300">Awaiting extend approval (see Approvals).</span>
        ) : null}
        {offer.status === 'extended' ? (
          <>
            <button type="button" onClick={() => accept.mutate()} disabled={accept.isPending} className="rounded bg-green-700 text-white text-xs px-3 py-1 disabled:opacity-50">Mark accepted</button>
            <button type="button" onClick={() => decline.mutate()} disabled={decline.isPending} className="rounded border border-gray-700 text-xs px-3 py-1 hover:bg-gray-900 disabled:opacity-50">Decline</button>
          </>
        ) : null}
        {offer.status === 'accepted' && !offer.convertedEmployeeId ? (
          <Link to="/recruitment/offers/$offerId/convert" params={{ offerId: offer.id }} className="rounded bg-green-700 text-white text-xs px-3 py-1">Convert to employee →</Link>
        ) : null}
        {offer.convertedEmployeeId ? <span className="text-xs text-green-400">Converted to employee.</span> : null}
      </div>
      <ApiErrorNotice error={approve.error ?? requestExtend.error ?? accept.error ?? decline.error} fallback="Action failed." compact />
    </div>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return (
    <div className={`rounded border p-6 text-center text-sm ${error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'}`}>
      {children}
    </div>
  );
}
